import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Check, ChevronsUpDown, Plus } from 'lucide-react';
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@nimbitlabs/ui';
import { cn } from '@nimbitlabs/ui';

interface Account {
  id: string;
  name: string;
  role?: string;
  department?: string;
}

interface AccountSwitcherProps {
  currentAccountId?: string;
  currentAccountName?: string;
  onLoadAccounts?: () => Promise<Account[]>;
  onSwitchAccount?: (accountId: string) => Promise<void>;
  createAccountPath?: string;
}

export function AccountSwitcher({
  currentAccountId,
  currentAccountName,
  onLoadAccounts,
  onSwitchAccount,
  createAccountPath = '/create-account',
}: AccountSwitcherProps) {
  const [open, setOpen] = useState(false);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSwitching, setIsSwitching] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    if (!onLoadAccounts) {
      setIsLoading(false);
      return;
    }

    try {
      const loadedAccounts = await onLoadAccounts();
      setAccounts(loadedAccounts);
    } catch (error) {
      console.error('Failed to load accounts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectAccount = async (accountId: string) => {
    // Don't switch if already on this account
    if (currentAccountId === accountId) {
      setOpen(false);
      return;
    }
    
    if (!onSwitchAccount) {
      setOpen(false);
      return;
    }

    setIsSwitching(true);
    setOpen(false);
    
    try {
      await onSwitchAccount(accountId);
    } catch (error) {
      console.error('Failed to switch account:', error);
    } finally {
      setIsSwitching(false);
    }
  };

  const currentAccountDisplay = accounts.find(a => a.id === currentAccountId);

  // Don't show switcher if loading or no accounts
  if (isLoading || accounts.length === 0) {
    return null;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-[240px] justify-between h-10 px-3 border-2 hover:border-primary/50 transition-colors bg-sidebar-accent text-sidebar-accent-foreground hover:bg-sidebar-accent/80"
          disabled={isSwitching}
        >
          <div className="flex items-center gap-2.5 truncate">
            <Building2 className="h-4 w-4 shrink-0 text-primary" />
            <span className="truncate font-semibold">
              {isSwitching ? 'Switching...' : (currentAccountDisplay?.name || currentAccountName || 'Select account...')}
            </span>
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search accounts..." className="h-10" />
          <CommandList>
            {[
              <CommandEmpty key="empty">No account found.</CommandEmpty>,
              <CommandGroup key="accounts" heading="Your Accounts">
                {accounts.map((account) => (
                  <CommandItem
                    key={account.id}
                    value={account.name}
                    onSelect={() => handleSelectAccount(account.id)}
                    className="px-3 py-3 cursor-pointer"
                  >
                    <Check
                      className={cn(
                        'mr-3 h-4 w-4 text-primary',
                        currentAccountId === account.id ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    <div className="flex flex-col flex-1 gap-1">
                      <span className="font-semibold text-sm">{account.name}</span>
                      {(account.role || account.department) && (
                        <span className="text-xs text-foreground/60 font-medium">
                          {account.role}{account.role && account.department && ' • '}{account.department}
                        </span>
                      )}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>,
              <CommandSeparator key="separator" />,
              <CommandGroup key="actions">
                <CommandItem
                  onSelect={() => {
                    setOpen(false);
                    navigate(createAccountPath);
                  }}
                  className="cursor-pointer px-3 py-3"
                >
                  <Plus className="mr-3 h-4 w-4 text-primary" />
                  <span className="font-semibold text-sm">Create New Account</span>
                </CommandItem>
              </CommandGroup>
            ]}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
